import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Icon,
  IconButton,
  Tooltip,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  Buildings,
  CaretLeft,
  CaretRight,
  XCircle,
  X,
  CheckCircle,
  UserCircle,
  ArrowUp,
  ArrowDown,
  CurrencyDollar,
} from 'phosphor-react';
import { useCompanies, ICompany } from '../../../hooks/useCompanies';
import { IData } from '../../../types/shared';
import { formatCNPJ, formatDate } from '../../../utils/FormatFunctions';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../components/SearchModeBar';
import { EmpresaDetailModal } from './EmpresaDetailModal';

export type FilterType =
  | 'ativas'
  | 'ativas_periodo'
  | 'inativas'
  | 'inativas_periodo'
  | 'entradas'
  | 'saidas'
  | 'com_folha'
  | 'fora_do_acessorias'
  | 'fora_da_dominio'
  | 'pis'
  | 'cofins'
  | 'csll'
  | 'irpj'
  | 'icms'
  | 'iss'
  | 'iss_retido'
  | 'simples_nacional'
  | null;

const filterTypeLabels: Record<string, string> = {
  ativas: 'Ativas',
  ativas_periodo: 'Ativas no Período',
  inativas: 'Inativas',
  inativas_periodo: 'Inativas com Período Informado',
  entradas: 'Contratos Iniciados',
  saidas: 'Contratos Finalizados',
  com_folha: 'Com Folha de Pagamento',
  fora_do_acessorias: 'Fora do Acessórias',
  fora_da_dominio: 'Fora da Domínio',
  pis: 'Com PIS',
  cofins: 'Com COFINS',
  csll: 'Com CSLL',
  irpj: 'Com IRPJ',
  icms: 'Com ICMS',
  iss: 'Com ISS',
  iss_retido: 'Com ISS Retido',
  simples_nacional: 'Simples Nacional',
};

const filterTypeIndicators: Record<
  string,
  { icon: React.ElementType; color: string; tooltip: string }
> = {
  ativas: {
    icon: CheckCircle,
    color: 'green.500',
    tooltip: 'Ativa',
  },
  ativas_periodo: {
    icon: CheckCircle,
    color: 'teal.500',
    tooltip: 'Ativa no Período',
  },
  inativas: {
    icon: XCircle,
    color: 'gray.500',
    tooltip: 'Inativa',
  },
  inativas_periodo: {
    icon: XCircle,
    color: 'red.500',
    tooltip: 'Inativa com Período Informado',
  },
  entradas: {
    icon: CheckCircle,
    color: 'cyan.500',
    tooltip: 'Contrato Iniciado',
  },
  saidas: {
    icon: XCircle,
    color: 'orange.500',
    tooltip: 'Contrato Finalizado',
  },
  com_folha: {
    icon: UserCircle,
    color: 'blue.500',
    tooltip: 'Com Folha de Pagamento',
  },
  pis: {
    icon: CurrencyDollar,
    color: 'blue.400',
    tooltip: 'Com PIS',
  },
  cofins: {
    icon: CurrencyDollar,
    color: 'green.400',
    tooltip: 'Com COFINS',
  },
  csll: {
    icon: CurrencyDollar,
    color: 'purple.400',
    tooltip: 'Com CSLL',
  },
  irpj: {
    icon: CurrencyDollar,
    color: 'orange.400',
    tooltip: 'Com IRPJ',
  },
  icms: {
    icon: CurrencyDollar,
    color: 'red.400',
    tooltip: 'Com ICMS',
  },
  iss: {
    icon: CurrencyDollar,
    color: 'cyan.400',
    tooltip: 'Com ISS',
  },
  iss_retido: {
    icon: CurrencyDollar,
    color: 'cyan.600',
    tooltip: 'Com ISS Retido',
  },
  simples_nacional: {
    icon: CurrencyDollar,
    color: 'teal.400',
    tooltip: 'Simples Nacional',
  },
};

interface EmpresasListSectionProps {
  filters?: Record<string, string>;
  filterType?: FilterType;
  onClearFilterType?: () => void;
}

type SortField = 'dt_cli_desde' | 'dt_cli_ate';
type SortDirection = 'asc' | 'desc' | null;

type SearchMode = 'ID' | 'Nome' | 'CNPJ';

export const EmpresasListSection = forwardRef<HTMLDivElement, EmpresasListSectionProps>(
  ({ filters = {}, filterType, onClearFilterType }, ref) => {
    const { getCompaniesDash } = useCompanies();

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchMode, setSearchMode] = useState<SearchMode>('ID');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEmpresa, setSelectedEmpresa] = useState<ICompany | null>(null);
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const handleSort = (field: SortField, direction: 'asc' | 'desc') => {
      if (sortField === field && sortDirection === direction) {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortField(field);
        setSortDirection(direction);
      }
      setCurrentPage(1);
    };

    const renderSortIcon = (field: SortField) => {
      const isActive = sortField === field;
      return (
        <span
          style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '2px' }}
        >
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSort(field, 'asc');
            }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <ArrowUp
              size={14}
              weight={isActive && sortDirection === 'asc' ? 'bold' : 'regular'}
              color={isActive && sortDirection === 'asc' ? '#3182ce' : '#a0aec0'}
            />
          </button>
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSort(field, 'desc');
            }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <ArrowDown
              size={14}
              weight={isActive && sortDirection === 'desc' ? 'bold' : 'regular'}
              color={isActive && sortDirection === 'desc' ? '#3182ce' : '#a0aec0'}
            />
          </button>
        </span>
      );
    };

    const handleRowClick = (empresa: ICompany) => {
      setSelectedEmpresa(empresa);
      onOpen();
    };

    const handleCloseModal = () => {
      setSelectedEmpresa(null);
      onClose();
    };

    // Debounce search term - only update after 3 seconds of no typing
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
        if (searchTerm !== debouncedSearchTerm) {
          setCurrentPage(1);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset page when parent filters change
    useEffect(() => {
      setCurrentPage(1);
    }, [filters, filterType]);

    // Reset page when searchMode changes
    useEffect(() => {
      setCurrentPage(1);
    }, [searchMode]);

    const companiesFilters = useMemo(() => {
      const baseFilters: Record<string, string | number> = {
        ...filters,
        page: currentPage,
        size: 50,
      };

      // Add search filter based on searchMode
      if (debouncedSearchTerm) {
        switch (searchMode) {
          case 'ID':
            baseFilters.external_id = debouncedSearchTerm;
            break;
          case 'Nome':
            baseFilters.razao_social = debouncedSearchTerm;
            break;
          case 'CNPJ':
            baseFilters.cnpj = debouncedSearchTerm.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
            break;
          default:
            break;
        }
      }

      // Add specific filter based on filterType
      if (filterType) {
        baseFilters.filter_type = filterType;
      }

      // Add sorting parameters
      if (sortField && sortDirection) {
        baseFilters.order_by = sortField;
        baseFilters.order_direction = sortDirection;
      }

      return baseFilters;
    }, [
      debouncedSearchTerm,
      searchMode,
      currentPage,
      filters,
      filterType,
      sortField,
      sortDirection,
    ]);

    const { data: companiesData, isFetching: isFetchingCompanies } = useQuery<IData<ICompany[]>>({
      queryKey: ['empresasListagemDash', companiesFilters],
      queryFn: getCompaniesDash,
    });

    const empresas = companiesData?.data?.items || [];

    return (
      <Box ref={ref} bg="white" p={6} borderRadius="xl" boxShadow="lg" mt={8}>
        <Heading size="md" mb={4} color="gray.700">
          <Flex align="center" gap={2}>
            <Icon as={Buildings} />
            Listagem de Empresas
            {filterType && (
              <Badge colorScheme="blue" fontSize="sm" ml={2}>
                {filterTypeLabels[filterType]}
                <IconButton
                  aria-label="Limpar filtro"
                  icon={<X size={12} />}
                  size="xs"
                  variant="ghost"
                  ml={1}
                  minW="auto"
                  h="auto"
                  p={0}
                  onClick={onClearFilterType}
                />
              </Badge>
            )}
          </Flex>
        </Heading>

        <Flex mb={4} gap={2} maxW="500px" flexDirection={{ base: 'column', sm: 'row' }}>
          <SearchModeBar
            modes={MODES_ID_NOME_CNPJ}
            mode={searchMode}
            onModeChange={m => setSearchMode(m as SearchMode)}
            value={searchTerm}
            onValueChange={setSearchTerm}
            height="40px"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setDebouncedSearchTerm(searchTerm);
                setCurrentPage(1);
              }
            }}
          />
        </Flex>

        {isFetchingCompanies ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>ID Externo</Th>
                  <Th>Nome / Razão Social</Th>
                  <Th display={{ base: 'none', md: 'table-cell' }}>CNPJ</Th>
                  <Th display={{ base: 'none', lg: 'table-cell' }}>Unidade</Th>
                  <Th display={{ base: 'none', md: 'table-cell' }}>
                    <Flex align="center">
                      Cliente Desde
                      {renderSortIcon('dt_cli_desde')}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex align="center">
                      Data Saída
                      {renderSortIcon('dt_cli_ate')}
                    </Flex>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {empresas.map(empresa => (
                  <Tr
                    key={empresa.id}
                    _hover={{ bg: 'blue.50', cursor: 'pointer' }}
                    onClick={() => handleRowClick(empresa)}
                  >
                    <Td>
                      <Flex align="center" gap={2}>
                        {filterType && filterTypeIndicators[filterType] && (
                          <Tooltip
                            label={filterTypeIndicators[filterType].tooltip}
                            placement="top"
                            hasArrow
                          >
                            <span>
                              <Icon
                                as={filterTypeIndicators[filterType].icon}
                                color={filterTypeIndicators[filterType].color}
                                weight="fill"
                                boxSize={5}
                              />
                            </span>
                          </Tooltip>
                        )}
                        <Badge colorScheme="blue">{empresa.external_id || '-'}</Badge>
                      </Flex>
                    </Td>
                    <Td maxW="400px">
                      <Flex align="center" gap={2}>
                        <Tooltip
                          label={empresa.razao_social || empresa.nome}
                          placement="top"
                          hasArrow
                        >
                          <Text fontWeight="medium" fontSize="sm" isTruncated maxW="380px">
                            {empresa.razao_social || empresa.nome}
                          </Text>
                        </Tooltip>
                      </Flex>
                    </Td>
                    <Td display={{ base: 'none', md: 'table-cell' }}>
                      <Text fontSize="sm">{formatCNPJ(empresa.cnpj)}</Text>
                    </Td>
                    <Td display={{ base: 'none', lg: 'table-cell' }}>
                      <Text fontSize="sm">{empresa.grupo_de_empresas || '-'}</Text>
                    </Td>
                    <Td display={{ base: 'none', md: 'table-cell' }}>
                      <Text fontSize="sm">
                        {empresa.dt_cli_desde ? formatDate(empresa.dt_cli_desde) : '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color={empresa.dt_cli_ate ? 'red.500' : 'gray.400'}>
                        {empresa.dt_cli_ate ? formatDate(empresa.dt_cli_ate) : '-'}
                      </Text>
                    </Td>
                  </Tr>
                ))}
                {empresas.length === 0 && (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Text color="gray.500">Nenhuma empresa encontrada</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            {companiesData?.data && (
              <Flex
                justify="space-between"
                align="center"
                mt={4}
                pt={4}
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <IconButton
                  aria-label="Página anterior"
                  icon={<CaretLeft size={20} />}
                  size="sm"
                  variant="outline"
                  isDisabled={!companiesData.data.previous_page}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                />
                <Text fontSize="sm" color="gray.600">
                  Página {companiesData.data.page} de {companiesData.data.pages}
                </Text>
                <IconButton
                  aria-label="Próxima página"
                  icon={<CaretRight size={20} />}
                  size="sm"
                  variant="outline"
                  isDisabled={!companiesData.data.next_page}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                />
              </Flex>
            )}
          </Box>
        )}

        <EmpresaDetailModal isOpen={isOpen} onClose={handleCloseModal} empresa={selectedEmpresa} />
      </Box>
    );
  },
);

EmpresasListSection.displayName = 'EmpresasListSection';
